import { useQueryClient } from '@tanstack/react-query'
import { BookOpenText, ChevronDown, ChevronRight } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'
import type { Sections } from '../../shared/sections'
import { Link } from 'react-router'
import { FormError } from '@/components/FormError'
import { ModernHome } from '@/components/home/ModernHome'
import { page } from '@/components/layout'
import { MomentsIcon, PeopleIcon, PhotosIcon, RecipesIcon } from '@/components/nav/icons'
import { Initial } from '@/components/paper'
import { Button } from '@/components/ui/button'
import { SettingsCog } from '@/components/people/SettingsCog'
import { mySections } from '@/lib/sections'
import { comingUp, useCircleCalendar, whenLabel } from '@/lib/calendar'
import { albumPhotoUrl } from '@/lib/albums'
import type { Me } from '@/lib/api'
import type { Family } from '@/lib/families'
import { usePageFrame } from '@/lib/frame'
import { momentPhotoUrl, useRecentMoments } from '@/lib/moments'
import {
  counted,
  dayLabel,
  didWhat,
  fitFaces,
  latelyDayQuery,
  latelyLine,
  localDay,
  monthDay,
  onlySections,
  onThisDayShown,
  useHome,
  useLatelyDay,
  useOnThisDay,
  yearsAgo,
  type HomeData,
  type LatelyDay,
  type LatelyRow,
  type OnThisDayYear,
} from '@/lib/home'
import { tint } from '@/lib/paper'
import { photoUrl } from '@/lib/photos'
import { useCurrentSpace } from '@/lib/spaces'
import { firstName } from '@/lib/tree'
import { cn } from '@/lib/utils'
import { EntryLine } from './CalendarPage'
import { PlaceHeader, WithFamily } from './FamilyPage'
import { Loader } from '@/components/Loader'
import { usePhotoLink } from '@/lib/photoMotion'

const link = 'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'

export const CircleHomePage = ({ me }: { me: Me }) => (
  <WithFamily>
    {(family) =>
      me.homeStyle === 'modern' ? <Modern family={family} me={me} /> : <CircleHome family={family} me={me} />
    }
  </WithFamily>
)

// In the Modern style the circle's name at the top left is the page's title, and picks where you are.
function Modern({ family, me }: { family: Family; me: Me }) {
  usePageFrame(family.id, { title: 'Home' })
  return <ModernHome family={family} me={me} />
}

// Where the app opens: a welcome, a line on what the circle added lately, a door into each place with a glimpse of
// what's inside, and the last few weeks as a quiet list of who added what. Everything shown is real and is only what
// you can see; nothing is counted toward anything.
function CircleHome({ family, me }: { family: Family; me: Me }) {
  usePageFrame(family.id, { title: 'Home' })
  const space = useCurrentSpace(family)
  const home = useHome(family.id, space?.id)
  const sections = mySections(me, family)
  // No one joins your own space or Everyone: that isn't news there.
  const data = home.data && onlySections(home.data, sections, family.personal || family.everyone)
  const tree = family.kind === 'family' ? 'the family tree' : 'the tree'
  return (
    <div className="flex flex-col gap-12 pt-6 pb-10 nav:gap-16 nav:pt-14">
      <div className={page}>
        <div className="flex items-start justify-between gap-3">
          <PlaceHeader family={family} title={`Welcome home, ${firstName(me.name)}.`} greeting>
            {data && (
              <p className="mt-2 max-w-2xl text-lg text-pretty text-muted-foreground">
                {latelyLine(data.lately, data.days, tree, family.personal, family.everyone)}
              </p>
            )}
          </PlaceHeader>
          {!family.everyone && <SettingsCog familyId={family.id} className="-mr-2" />}
        </div>
      </div>
      {home.error && (
        <div className={page}>
          <FormError error={home.error} />
        </div>
      )}
      {data && (
        <>
          <Doors family={family} data={data} spaceId={space?.id} sections={sections} />
          {sections.calendar && <ComingUp family={family} spaceId={space?.id} />}
          <OnThisDay family={family} spaceId={space?.id} sections={sections} />
          <Lately data={data} family={family} spaceId={space?.id} />
        </>
      )}
    </div>
  )
}

// One door into each place, showing a little of what's inside.
function Doors({
  family,
  data,
  spaceId,
  sections,
}: {
  family: Family
  data: HomeData
  spaceId?: string
  sections: Sections
}) {
  const { totals, previews } = data
  const base = `/circle/${family.id}`
  // New is what others shared with you over the same weeks as the rest of the page; your own aren't news to you. The
  // pictures are the newest moments you can read, your own included, as the other doors show.
  const [since] = useState(() => Date.now() - data.days * 24 * 60 * 60 * 1000)
  const moments = useRecentMoments(family.id, { since, spaceId, enabled: sections.moments }).data
  const onTree = [
    totals.people > 0 && counted(totals.people, 'person', 'people'),
    totals.pets > 0 && counted(totals.pets, 'pet'),
  ].filter(Boolean)
  const doors = [
    {
      key: 'people' as const,
      to: `${base}/people`,
      label: 'People',
      line: onTree.length
        ? `${onTree.join(' and ')} on the tree · ${counted(totals.members, 'member')}`
        : totals.members > 1
          ? `${counted(totals.members, 'member')}. Start the family tree.`
          : 'Every circle starts with one archivist. That’s you.',
      glimpse: previews.people.length ? (
        <Faces people={previews.people} onTree={totals.people + totals.pets} />
      ) : (
        <Blank icon={<PeopleIcon className="size-9" />} index={3} />
      ),
    },
    {
      key: 'photos' as const,
      to: `${base}/photos`,
      label: 'Photos',
      line: totals.photos
        ? `${counted(totals.photos, 'photo')} in ${counted(totals.albums, 'album')}`
        : 'Open the first album.',
      glimpse: previews.photos.length ? (
        <Mosaic urls={previews.photos.map((x) => albumPhotoUrl(x.ref, 'thumb'))} />
      ) : (
        <Blank icon={<PhotosIcon className="size-9" />} index={2} />
      ),
    },
    {
      key: 'moments' as const,
      to: `${base}/moments`,
      label: 'Moments',
      line: moments?.count
        ? `${counted(moments.count, 'new moment')} shared with you. Read them.`
        : totals.moments
          ? counted(totals.moments, 'moment')
          : 'Every circle has stories worth keeping.',
      glimpse: previews.moments.length ? (
        <Mosaic urls={previews.moments.map((id) => momentPhotoUrl(id, 'thumb'))} />
      ) : (
        <Blank icon={<MomentsIcon className="size-9" />} index={4} />
      ),
    },
    {
      key: 'recipes' as const,
      to: `${base}/recipes`,
      label: 'Recipes',
      line: totals.recipes
        ? `${counted(totals.recipes, 'recipe')} in ${counted(totals.books, 'book')}`
        : 'Every circle has a recipe worth keeping. Photograph the first card.',
      glimpse: previews.recipes.length ? (
        <Mosaic urls={previews.recipes.map((id) => photoUrl(id, 'thumb'))} />
      ) : (
        <Blank icon={<RecipesIcon className="size-9" />} index={1} />
      ),
    },
  ].filter((door) => sections[door.key])
  return (
    <nav aria-label="Your circle" className={page}>
      <ul className="grid gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
        {doors.map((door) => (
          <li key={door.key}>
            <Link
              to={door.to}
              className={cn(
                'group flex items-center gap-4 rounded-[1.5rem] p-2 transition-colors hover:bg-secondary/60 sm:flex-col sm:items-stretch sm:gap-4 sm:p-0 sm:hover:bg-transparent',
                link,
              )}
            >
              <span
                aria-hidden="true"
                className="block size-22 shrink-0 overflow-hidden rounded-[1.25rem] shadow-paper transition-[translate,box-shadow] duration-300 ease-settle group-hover:translate-y-[-3px] group-hover:shadow-lift sm:aspect-[4/3] sm:size-auto sm:rounded-[1.5rem]"
              >
                {door.glimpse}
              </span>
              <span className="flex min-w-0 flex-1 items-center gap-2 sm:px-1">
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-lg font-semibold tracking-tight">{door.label}</span>
                  <span className="text-sub text-pretty text-muted-foreground">{door.line}</span>
                </span>
                <ChevronRight
                  className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )
}

// Up to four pictures sharing one frame: one fills it, two sit side by side, three put the newest on the left.
function Mosaic({ urls }: { urls: string[] }) {
  const shown = urls.slice(0, 4)
  return (
    <span
      className={cn(
        'grid size-full gap-0.5 bg-background',
        shown.length > 1 && 'grid-cols-2',
        shown.length > 2 && 'grid-rows-2',
      )}
    >
      {shown.map((url, i) => (
        <img
          key={url}
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          className={cn('size-full min-h-0 object-cover', shown.length === 3 && i === 0 && 'row-span-2')}
        />
      ))}
    </span>
  )
}

function Blank({ icon, index }: { icon: ReactNode; index: number }) {
  return <span className={cn('grid size-full place-items-center text-muted-foreground', tint(index))}>{icon}</span>
}

// Faces from the tree, evenly spaced on a plain ground: up to six on the large card and four on a phone's small one,
// the last circle counting everyone else when there are more.
function Faces({ people, onTree }: { people: HomeData['previews']['people']; onTree: number }) {
  return (
    <span className={cn('@container grid size-full place-items-center', tint(2))}>
      <FaceGrid people={people} onTree={onTree} slots={4} className="grid-cols-2 gap-1.5 sm:hidden" face="size-8" />
      <FaceGrid
        people={people}
        onTree={onTree}
        slots={6}
        className="hidden grid-cols-3 gap-[4cqw] sm:grid"
        face="size-[22cqw]"
      />
    </span>
  )
}

function FaceGrid({
  people,
  onTree,
  slots,
  className,
  face,
}: {
  people: HomeData['previews']['people']
  onTree: number
  slots: number
  className: string
  face: string
}) {
  const { shown, more } = fitFaces(people, onTree, slots)
  const count = shown.length + (more ? 1 : 0)
  return (
    <span
      className={cn('grid', className, count === 1 && 'grid-cols-1', (count === 2 || count === 4) && 'grid-cols-2')}
    >
      {shown.map((person, i) => (
        <Initial key={person.id} name={person.name} portrait={person.portrait} index={i} className={face} />
      ))}
      {more > 0 && (
        <span
          className={cn(
            'grid place-items-center rounded-full bg-background text-xs font-semibold text-foreground @[12rem]:text-lg',
            face,
          )}
        >
          +{more}
        </span>
      )}
    </span>
  )
}

// The next few days that are about someone (a birthday, a remembrance day) in the month ahead, from the calendar.
// Hidden when there are none; On this day has its own place just below.
const COMING_UP = { days: 30, shown: 3 }
function ComingUp({ family, spaceId }: { family: Family; spaceId?: string }) {
  const [today] = useState(localDay)
  const calendar = useCircleCalendar(family.id, spaceId, today, COMING_UP.days)
  const next = (calendar.data?.entries ?? []).filter(comingUp).slice(0, COMING_UP.shown)
  if (!next.length) return null
  return (
    <section className={cn(page, 'flex flex-col gap-3')} aria-labelledby="coming-up">
      <div className="flex max-w-3xl items-baseline justify-between gap-4">
        <h2 id="coming-up" className="text-title">
          Coming up
        </h2>
        <Link
          to={`/circle/${family.id}/calendar`}
          className={cn(
            'flex min-h-11 items-center gap-1 rounded-lg text-sub font-semibold text-muted-foreground hover:text-foreground',
            link,
          )}
        >
          Calendar
          <ChevronRight className="size-4" aria-hidden />
        </Link>
      </div>
      <ol className="flex max-w-3xl flex-col divide-y divide-border/70">
        {next.map((entry, i) => (
          <li key={i} className="flex items-center gap-3 py-1.5">
            <p
              className={cn(
                'w-24 shrink-0 text-sub font-semibold',
                entry.kind === 'birthday' ? 'text-warm-foreground' : 'text-muted-foreground',
              )}
            >
              {whenLabel(entry.on, today)}
            </p>
            <div className="min-w-0 flex-1">
              <EntryLine entry={entry} familyId={family.id} />
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

// Photos taken and moments that happened on this date in earlier years, newest year first. Only things with a full
// date are here, so nothing is placed on a day it may not have been. Left out entirely when there's nothing.
function OnThisDay({ family, spaceId, sections }: { family: Family; spaceId?: string; sections: Sections }) {
  const [day] = useState(localDay)
  const on = useOnThisDay(family.id, spaceId, day, sections.photos || sections.moments)
  const years = on.data ? onThisDayShown(on.data, sections) : []
  if (!years.length) return null
  return (
    <section className={cn(page, 'flex flex-col gap-5')} aria-labelledby="on-this-day">
      <div className="flex items-baseline justify-between gap-4">
        <h2 id="on-this-day" className="text-title">
          On this day
        </h2>
        <p className="text-sub text-muted-foreground">{monthDay(day)}</p>
      </div>
      <Button asChild variant="secondary" size="sm" className="-mt-2 self-start">
        <Link to={`/circle/${family.id}/on-this-day/story?day=${day}`}>
          <BookOpenText aria-hidden /> Storybook version
        </Link>
      </Button>
      <ol className="flex flex-col gap-6">
        {years.map((year) => (
          <ThatYear key={year.year} year={year} day={day} family={family} />
        ))}
      </ol>
    </section>
  )
}

// A year's moments come first: there are fewer of them, and each is a story.
function ThatYear({ year, day, family }: { year: OnThisDayYear; day: string; family: Family }) {
  const { photos, moments } = year
  const morePhotos = photos.count - photos.shown.length
  const moreMoments = moments.count - moments.shown.length
  const open = usePhotoLink()
  const label = `${yearsAgo(year.year, day)}, ${year.year}`
  return (
    <li className="flex flex-col gap-2" aria-label={label}>
      <p className="text-sub text-muted-foreground">
        <span className="font-semibold text-foreground">{yearsAgo(year.year, day)}</span> · {year.year}
      </p>
      <ul className="flex gap-2 overflow-x-auto pb-1" aria-label={`From ${label}`}>
        {moments.shown.map((moment, i) => (
          <li key={moment.id} className="shrink-0">
            <Link
              to={`/moment/${moment.id}`}
              className={cn('flex h-32 w-44 flex-col overflow-hidden rounded-2xl sm:h-40 sm:w-52', tint(i + 1), link)}
            >
              {moment.photo && (
                <img
                  src={momentPhotoUrl(moment.photo, 'thumb')}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="min-h-0 w-full flex-1 bg-secondary object-cover"
                />
              )}
              <span className={cn('flex flex-col gap-0.5 p-3', !moment.photo && 'flex-1 justify-end')}>
                <span className="line-clamp-2 leading-snug font-semibold text-balance">{moment.title}</span>
                <span className="text-xs text-muted-foreground">
                  {moment.by.you ? 'Your moment' : `${firstName(moment.by.name)}’s moment`}
                </span>
              </span>
            </Link>
          </li>
        ))}
        {moreMoments > 0 && (
          <li className="shrink-0">
            <Link
              to={`/circle/${family.id}/moments`}
              className={cn(
                'grid h-32 w-28 place-items-center rounded-2xl bg-secondary p-3 text-center text-sm font-semibold sm:h-40',
                link,
              )}
            >
              {counted(moreMoments, 'more moment')}
            </Link>
          </li>
        )}
        {photos.shown.map((photo, i) => {
          const last = morePhotos > 0 && i === photos.shown.length - 1
          return (
            <li key={photo.id} className="relative shrink-0">
              <Link
                {...(last ? { to: `/circle/${family.id}/photos?year=${year.year}` } : open(photo.id))}
                aria-label={last ? `${counted(morePhotos, 'more photo')} from ${year.year}` : undefined}
                className={cn('block overflow-hidden rounded-2xl', link)}
              >
                <img
                  src={albumPhotoUrl(photo.ref, 'thumb')}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="size-32 bg-secondary object-cover sm:size-40"
                />
                {last && (
                  <span className="absolute inset-0 grid place-items-center bg-black/45 text-lg font-semibold text-white">
                    +{morePhotos}
                  </span>
                )}
              </Link>
            </li>
          )
        })}
      </ul>
    </li>
  )
}

// Who did what over the last few weeks: one row per person per day, newest day first, with everyone else above your
// own. A row opens in place to show what was added, each thing a way in. The first rows show; the rest wait under
// "Earlier".
const FIRST_ROWS = 20

function Lately({ data, family, spaceId }: { data: HomeData; family: Family; spaceId?: string }) {
  const { totals, rows, days } = data
  const [earlier, setEarlier] = useState(false)
  const nothingYet = !totals.recipes && !totals.photos && !totals.people && !totals.pets
  const shown = earlier ? rows : rows.slice(0, FIRST_ROWS)
  return (
    <section className={cn(page, 'flex flex-col gap-2')} aria-labelledby="lately">
      <div className="flex max-w-3xl items-baseline justify-between gap-4">
        <h2 id="lately" className="text-title">
          Lately
        </h2>
        <p className="text-sub text-muted-foreground">The last {days} days</p>
      </div>
      {rows.length > 0 ? (
        <>
          <ol className="flex max-w-3xl flex-col divide-y divide-border/70">
            {shown.map((row, i) => (
              <LatelyItem key={`${row.by.id}-${row.day}`} row={row} index={i} family={family} spaceId={spaceId} />
            ))}
          </ol>
          {rows.length > FIRST_ROWS && !earlier && (
            <button
              type="button"
              onClick={() => setEarlier(true)}
              className={cn(
                'flex min-h-11 max-w-3xl items-center gap-1.5 self-start rounded-lg font-semibold text-muted-foreground hover:text-foreground',
                link,
              )}
            >
              Earlier
              <ChevronDown className="size-4" aria-hidden />
            </button>
          )}
        </>
      ) : (
        <p className="pt-2 whisper">
          {nothingYet ? 'Every circle starts with one archivist. That’s you.' : 'Nothing new lately.'}
        </p>
      )}
    </section>
  )
}

function LatelyItem({
  row,
  index,
  family,
  spaceId,
}: {
  row: LatelyRow
  index: number
  family: Family
  spaceId?: string
}) {
  const [open, setOpen] = useState(false)
  const client = useQueryClient()
  const id = useId()
  // Only someone joining has nothing to open.
  const opens = row.photos + row.recipes + row.people + row.pets + row.moments > 0
  const prefetch = () => opens && void client.prefetchQuery(latelyDayQuery(family.id, row, spaceId))
  const line = (
    <span>
      <span className="font-semibold">{row.by.you ? 'You' : row.by.name}</span> {didWhat(row, family.kind === 'family')}{' '}
      <span className="text-sub whitespace-nowrap text-muted-foreground">· {dayLabel(row.day)}</span>
    </span>
  )
  return (
    <li className="flex gap-3.5 py-3">
      <Initial name={row.by.name} portrait={row.by.portrait} index={index} className="mt-0.5 size-10" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {opens ? (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={id}
            onClick={() => setOpen(!open)}
            onPointerEnter={prefetch}
            onFocus={prefetch}
            className={cn(
              'flex min-h-11 items-center gap-2 rounded-lg text-left leading-snug text-pretty hover:[&>span:first-child]:underline hover:[&>span:first-child]:underline-offset-4',
              link,
            )}
          >
            {line}
            <ChevronDown
              aria-hidden
              className={cn(
                'ml-auto size-5 shrink-0 text-muted-foreground transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </button>
        ) : (
          <p className="flex min-h-11 items-center leading-snug text-pretty">{line}</p>
        )}
        <div id={id} hidden={!open}>
          {open && <DayDetail row={row} family={family} spaceId={spaceId} />}
        </div>
      </div>
    </li>
  )
}

// What one person added that day: photos by album, recipes, people and moments, each a way in.
function DayDetail({ row, family, spaceId }: { row: LatelyRow; family: Family; spaceId?: string }) {
  const day = useLatelyDay(family.id, row, spaceId, true)
  if (day.error) return <FormError error={day.error} />
  if (!day.data) return <Loader label="Opening" />
  // Only what the row counts: a section the owner switched off counts nothing, so it shows nothing here either.
  const { albums, recipes, people, moments } = {
    albums: row.photos ? day.data.albums : [],
    recipes: row.recipes ? day.data.recipes : [],
    people: row.people + row.pets ? day.data.people : [],
    moments: row.moments ? day.data.moments : [],
  }
  const peopleMore = row.people + row.pets - people.length
  const recipesMore = row.recipes - recipes.length
  return (
    <div className="flex flex-col gap-4 pb-2">
      {albums.map((album) => {
        // The photos added to no album come together, and open where those photos are.
        const to = album.loose ? `/circle/${family.id}/photos?loose=1` : `/album/${album.id}`
        return (
          <Group
            key={album.id}
            label={album.loose ? counted(album.count, 'photo') : `${counted(album.count, 'photo')} in`}
            title={album.loose ? 'not in an album' : album.title}
            to={to}
          >
            <Thumbs album={album} to={to} />
          </Group>
        )
      })}
      {recipes.length > 0 && (
        <Group
          label={counted(row.recipes, 'recipe')}
          more={recipesMore > 0 ? { n: recipesMore, to: `/circle/${family.id}/recipes` } : undefined}
        >
          <Recipes recipes={recipes} />
        </Group>
      )}
      {people.length > 0 && (
        <Group
          label={family.kind === 'family' ? 'On the family tree' : 'On the tree'}
          more={peopleMore > 0 ? { n: peopleMore, to: `/circle/${family.id}/people` } : undefined}
        >
          <Added people={people} />
        </Group>
      )}
      {moments.length > 0 && (
        <Group label={counted(row.moments, 'moment')}>
          <ul className="flex flex-col" aria-label="Moments written">
            {moments.map((moment) => (
              <li key={moment.id}>
                <Link
                  to={`/moment/${moment.id}`}
                  className={cn(
                    'flex min-h-11 items-center rounded-lg font-semibold underline-offset-4 hover:underline',
                    link,
                  )}
                >
                  {moment.title}
                </Link>
              </li>
            ))}
          </ul>
        </Group>
      )}
    </div>
  )
}

function Group({
  label,
  title,
  to,
  more,
  children,
}: {
  label: string
  title?: string
  to?: string
  more?: { n: number; to: string }
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <p className="flex flex-wrap items-center gap-x-1 text-sub text-muted-foreground">
        {label}
        {title && to && (
          <Link
            to={to}
            className={cn(
              'inline-flex min-h-11 items-center rounded-lg font-semibold text-foreground underline-offset-4 hover:underline',
              link,
            )}
          >
            {title}
          </Link>
        )}
        {more && (
          <Link
            to={more.to}
            className={cn('inline-flex min-h-11 items-center rounded-lg underline-offset-4 hover:underline', link)}
          >
            · and {more.n.toLocaleString('en-US')} more
          </Link>
        )}
      </p>
      {children}
    </div>
  )
}

function Thumbs({ album, to }: { album: LatelyDay['albums'][number]; to: string }) {
  const more = album.count - album.photos.length
  const open = usePhotoLink()
  return (
    <ul
      className="flex gap-1.5 overflow-x-auto pb-0.5"
      aria-label={album.loose ? 'Photos added, not in an album' : `Photos added to ${album.title}`}
    >
      {album.photos.map((photo, i) => (
        <li key={photo.id} className="relative shrink-0">
          <Link
            {...(more > 0 && i === album.photos.length - 1 ? { to } : open(photo.id))}
            className={cn('block overflow-hidden rounded-xl', link)}
          >
            <img
              src={albumPhotoUrl(photo.ref, 'thumb')}
              alt=""
              loading="lazy"
              decoding="async"
              className="size-20 bg-secondary object-cover sm:size-24"
            />
            {more > 0 && i === album.photos.length - 1 && (
              <span className="absolute inset-0 grid place-items-center rounded-xl bg-black/45 font-semibold text-white">
                +{more}
              </span>
            )}
          </Link>
        </li>
      ))}
    </ul>
  )
}

function Recipes({ recipes }: { recipes: LatelyDay['recipes'] }) {
  return (
    <ul className="flex gap-1.5 overflow-x-auto pb-0.5" aria-label="Recipes added">
      {recipes.map((recipe, i) => (
        <li key={recipe.id} className="shrink-0">
          <Link
            to={`/recipe/${recipe.id}`}
            aria-label={recipe.title}
            className={cn('block aspect-[4/5] w-20 overflow-hidden rounded-xl sm:w-24', tint(i), link)}
          >
            {recipe.cover ? (
              <img
                src={photoUrl(recipe.cover, 'thumb')}
                alt=""
                loading="lazy"
                decoding="async"
                className="size-full object-cover"
              />
            ) : (
              <span className="grid size-full place-items-center p-2 text-center text-xs font-semibold text-balance">
                {recipe.title}
              </span>
            )}
          </Link>
        </li>
      ))}
    </ul>
  )
}

function Added({ people }: { people: LatelyDay['people'] }) {
  return (
    <ul className="flex gap-3 overflow-x-auto pb-0.5" aria-label="Added to the tree">
      {people.map((person, i) => (
        <li key={person.id} className="w-16 shrink-0">
          <Link to={`/person/${person.id}`} className={cn('flex flex-col items-center gap-1.5 rounded-xl', link)}>
            <Initial name={person.name} portrait={person.portrait} index={i} className="size-14 text-base" />
            <span className="w-full truncate text-center text-xs text-muted-foreground">{firstName(person.name)}</span>
          </Link>
        </li>
      ))}
    </ul>
  )
}
